/**
 * Tools guide — default tools, 3rd party integrations, tool assignment at
 * each level (teammate/task/run), and end-user OAuth connections.
 */

export const skillsContent = `
Tools are the capabilities your teammates can use. There are two kinds:

- **Default tools** — built-in capabilities always available (memory, task history, file write)
- **3rd Party Integrations** — external services like Gmail, Slack, Google Ads that you connect via OAuth

## List Available Integrations

\`\`\`python
from m8tes import M8tes

client = M8tes()

page = client.apps.list()
for app in page.data:
    status = "connected" if app.connected else "not connected"
    print(f"{app.name} ({app.category}) — {status}")
\`\`\`

\`\`\`bash
curl https://m8tes.ai/api/v2/apps \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Assign Tools to a Teammate

Set the base set of tools when creating a teammate. These tools are available for every run:

\`\`\`python
bot = client.teammates.create(
    name="Marketing Bot",
    tools=["slack", "google-ads", "google-sheets"],
    instructions="Manage marketing campaigns and report on metrics"
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Marketing Bot", "tools": ["slack", "google-ads", "google-sheets"]}'
\`\`\`

## Override Tools Per Task

Tasks can override the teammate's tool set:

\`\`\`python
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Send the weekly newsletter via Slack",
    tools=["slack"],  # Only Slack for this task
)
\`\`\`

## Override Tools Per Run

Restrict or expand tools for a specific run without changing the teammate or task:

\`\`\`python
run = client.runs.create(
    teammate_id=bot.id,
    message="Send the weekly newsletter",
    tools=["slack"],  # Only Slack for this run
    stream=False,
)
\`\`\`

## Tool Configuration Levels

| Level | Set via | Scope |
|-------|---------|-------|
| **Teammate** | \`teammates.create(tools=[...])\` | Base tools for all runs |
| **Task** | \`tasks.create(tools=[...])\` | Override for this task's runs |
| **Run** | \`runs.create(tools=[...])\` | Override for this single run |

If a run specifies \`tools\`, it overrides the teammate and task defaults. If not specified, the teammate's tools are used.

## Connecting 3rd Party Integrations

Connect integrations through the [Developer Dashboard](https://m8tes.ai/developer) — OAuth flows are handled automatically.

Check connection status:

\`\`\`python
page = client.apps.list()
connected = [app.name for app in page.data if app.connected]
not_connected = [app.name for app in page.data if not app.connected]
print(f"Ready: {connected}")
print(f"Need setup: {not_connected}")
\`\`\`

## End-User Connections (Multi-Tenant OAuth)

Connect apps programmatically for your end-users. Each customer authorizes their own Gmail, Slack, etc.

### 1. Initiate Connection

\`\`\`python
conn = client.apps.connect(
    "gmail",
    redirect_uri="https://yourapp.com/callback",
    user_id="cust_123"
)
# Redirect your user to this URL
print(conn.authorization_url)
# Store conn.connection_id — you'll need it after the OAuth redirect
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/apps/gmail/connect \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"redirect_uri": "https://yourapp.com/callback", "user_id": "cust_123"}'
\`\`\`

### 2. Complete Connection

After the user completes OAuth and is redirected back:

\`\`\`python
result = client.apps.connect_complete(
    "gmail",
    connection_id=conn.connection_id,
    user_id="cust_123"
)
print(result.status)  # "connected"
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/apps/gmail/connect/complete \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"connection_id": "conn_abc123", "user_id": "cust_123"}'
\`\`\`

### 3. Check End-User Status

\`\`\`python
page = client.apps.list(user_id="cust_123")
for app in page.data:
    print(f"{app.name}: {'connected' if app.connected else 'not connected'}")
\`\`\`

### 4. Disconnect

\`\`\`python
client.apps.disconnect("gmail", user_id="cust_123")
\`\`\`

### How It Works

- Each end-user gets their own OAuth connection scoped by \`user_id\`
- When running tasks for that user, the teammate automatically uses their connected accounts
- Account-level connections (no \`user_id\`) act as fallback for users without their own connection

## What's Next

- [Teammates](/docs/teammates) — create reusable personas with tools
- [Permissions](/docs/permissions) — control which tools require approval
- [Users](/docs/users) — multi-tenancy and data isolation
`;
